import { MutableRefObject, useRef, useState } from 'react';

import { ExportOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Button, Dropdown, Popconfirm, Tooltip, Upload, notification } from 'antd';
import axios from 'axios';

interface EOBManageButtonProps {
  record: any;
  onSuccess: () => void;
}

const EOBManageButton = ({ record, onSuccess }: EOBManageButtonProps) => {
  const [eobLoading, setEobLoading] = useState<string | null>(null);
  const eobFileInputRef = useRef<HTMLInputElement | null>(
    null
  ) as MutableRefObject<HTMLInputElement | null>;
  const [toast, toastHolder] = notification.useNotification();

  const handleRemoveEOB = async (id: string) => {
    try {
      await axios.post(`assistance_requests/${id}/delete_eob`);
      onSuccess();
      toast.success({
        message: 'EOB deleted successfully',
        description: 'The EOB has been deleted successfully.',
      });
    } catch (error) {
      toast.error({
        message: 'Failed to delete EOB',
        description: 'The EOB could not be deleted. Please try again.',
      });
    }
  };

  const handleReplaceEOB = (recordId: string) => {
    if (!eobFileInputRef.current) {
      const input = document.createElement('input');
      input.type = 'file';
      input.style.display = 'none';
      input.accept = 'application/pdf, image/*';
      input.onchange = (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (file) {
          const formData = new FormData();
          formData.append('file', file);

          setEobLoading(recordId);
          axios
            .post(`assistance_requests/${recordId}/upload_eob`, formData, {
              headers: {
                Authorization: `${localStorage.getItem('Authorization')}`,
                ProgramName: 'wilate',
                'Content-Type': 'multipart/form-data',
              },
            })
            .then(() => {
              onSuccess();
              toast.success({
                message: 'EOB replaced successfully',
                description: 'The EOB has been replaced with the new file.',
              });
            })
            .catch(() => {
              toast.error({
                message: 'Failed to replace EOB',
                description: 'The EOB could not be replaced. Please try again.',
              });
            })
            .finally(() => {
              setEobLoading(null);
              input.value = '';
            });
        }
      };
      document.body.appendChild(input);
      eobFileInputRef.current = input;
    }

    eobFileInputRef.current.click();
  };

  const onEOBMenuClick = (e: any) => {
    if (e.key === 'replace') {
      handleReplaceEOB(record.id);
    }
  };

  return (
    <>
      {toastHolder}
      <div className="flex items-center justify-center gap-x-2">
        <span>{record.status_string}</span>
        {record.status_string === 'EOB Pending' && (
          <Upload
            action={`${axios.defaults.baseURL}/assistance_requests/${record.id}/upload_eob`}
            showUploadList={false}
            headers={{
              Authorization: `${localStorage.getItem('Authorization')}`,
            }}
            maxCount={1}
            accept="application/pdf, image/*"
            onChange={(info) => {
              if (info.file.status === 'uploading') {
                setEobLoading(record.id);
              } else if (info.file.status === 'done') {
                setEobLoading(null);
                onSuccess();
              } else if (info.file.status === 'error') {
                setEobLoading(null);
              }
            }}
          >
            <Button
              size="small"
              loading={eobLoading === record.id}
              disabled={eobLoading === record.id}
            >
              Upload EOB{' '}
              <Tooltip title="Please upload EOBs as PDF or image files only. Editable formats like Word or text files are not accepted.">
                <InfoCircleOutlined />
              </Tooltip>
            </Button>
          </Upload>
        )}
        {record.status_string === 'EOB in Review' && (
          <div className="flex items-center justify-center gap-x-2">
            <a target="_blank" href={record.eob_url}>
              <ExportOutlined />
            </a>
            <Dropdown.Button
              trigger={['click']}
              size="small"
              loading={eobLoading === record.id}
              menu={{
                items: [
                  {
                    key: 'replace',
                    label: (
                      <span className="flex items-center gap-x-1.5">
                        Replace EOB{' '}
                        <Tooltip title="Please upload EOBs as PDF or image files only. Editable formats like Word or text files are not accepted.">
                          <InfoCircleOutlined />
                        </Tooltip>
                      </span>
                    ),
                  },
                  {
                    key: 'delete',
                    label: (
                      <Popconfirm
                        placement="top"
                        arrowContent={null}
                        trigger={['click']}
                        title="Are you sure you want to remove this EOB?"
                        onConfirm={() => handleRemoveEOB(record.id)}
                      >
                        <span onClick={(e) => e.stopPropagation()}>Remove EOB</span>
                      </Popconfirm>
                    ),
                  },
                ],
                onClick: (e) => onEOBMenuClick(e),
              }}
            >
              Modify EOB
            </Dropdown.Button>
          </div>
        )}
      </div>
    </>
  );
};

export default EOBManageButton;
